import { useState, useEffect } from 'react';

type Lang = 'EN' | 'TR';

interface NavContent {
  links: { label: string; href: string }[];
  cta: string;
}

const content: Record<Lang, NavContent> = {
  EN: {
    links: [
      { label: 'Work', href: '#showreel' },
      { label: 'Services', href: '#what-we-build' },
      { label: 'Process', href: '#how-it-works' },
      { label: 'Technology', href: '#technology' },
      { label: 'Contact', href: '#contact' },
    ],
    cta: 'Start a Project',
  },
  TR: {
    links: [
      { label: 'Çalışmalar', href: '#showreel' },
      { label: 'Hizmetler', href: '#what-we-build' },
      { label: 'Süreç', href: '#how-it-works' },
      { label: 'Teknoloji', href: '#technology' },
      { label: 'İletişim', href: '#contact' },
    ],
    cta: 'Proje Başlat',
  },
};

export type { Lang };
export { content as navContent };

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [lang, setLang] = useState<Lang>('EN');

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    (window as unknown as Record<string, unknown>).__KODO_LANG__ = lang;
    window.dispatchEvent(new CustomEvent('kodo-lang-change', { detail: lang }));
  }, [lang]);

  const handleNav = (href: string) => {
    setMenuOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const nav = content[lang];

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-white/95 backdrop-blur-md border-b border-gray-100 shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="mx-auto px-8 md:px-16 h-24 flex items-center justify-between">
        {/* Logo */}
        <a
          href="#hero"
          onClick={(e) => { e.preventDefault(); handleNav('#hero'); }}
          className="flex items-center cursor-pointer flex-shrink-0"
        >
          <div className="bg-white rounded-xl px-3 py-2 flex items-center justify-center" style={{ boxShadow: '0 1px 8px rgba(0,0,0,0.06)' }}>
            <img
              src="https://static.readdy.ai/image/5f8ccebbf933f3139676e12de0a0cc44/2d5342e9bc248f6f9498ec9df80e9d39.png"
              alt="KODO Logo"
              className="h-20 w-auto object-contain"
            />
          </div>
        </a>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-8">
          {nav.links.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => { e.preventDefault(); handleNav(link.href); }}
              className="text-gray-500 hover:text-gray-900 text-sm font-medium tracking-wide transition-colors duration-300 cursor-pointer whitespace-nowrap"
            >
              {link.label}
            </a>
          ))}
        </div>

        {/* Iris Logo */}
        <a
          href="https://iris.kodonext.com"
          target="_blank"
          rel="noopener noreferrer"
          className="hidden md:flex items-center opacity-80 hover:opacity-100 transition-opacity duration-300"
        >
          <img src="/iris-logo.png" alt="Iris" className="h-[65px] w-auto object-contain" />
        </a>

        {/* Right: Lang + CTA */}
        <div className="hidden md:flex items-center gap-4 flex-shrink-0">
          <div className="flex items-center gap-1 bg-gray-100 rounded-full p-1">
            {(['TR', 'EN'] as Lang[]).map((l) => (
              <button
                key={l}
                onClick={() => setLang(l)}
                className={`px-3 py-1 text-xs font-semibold tracking-wider uppercase rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap ${
                  lang === l
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-400 hover:text-gray-600'
                }`}
              >
                {l}
              </button>
            ))}
          </div>
          <a
            href="#contact"
            onClick={(e) => { e.preventDefault(); handleNav('#contact'); }}
            className="flex items-center gap-2 px-5 py-2.5 bg-gray-900 text-white text-sm font-semibold tracking-wide rounded-full hover:bg-gray-700 transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            {nav.cta}
            <i className="ri-arrow-right-up-line text-sm w-4 h-4 flex items-center justify-center"></i>
          </a>
        </div>

        {/* Hamburger */}
        <button
          className="md:hidden w-10 h-10 flex items-center justify-center text-gray-900 cursor-pointer"
          onClick={() => setMenuOpen(!menuOpen)}
        >
          <i className={`text-xl ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
        </button>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 px-8 py-6 flex flex-col gap-5">
          {nav.links.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => { e.preventDefault(); handleNav(link.href); }}
              className="text-gray-600 hover:text-gray-900 text-sm font-medium tracking-wide transition-colors cursor-pointer"
            >
              {link.label}
            </a>
          ))}
          <a
            href="https://iris.kodonext.com"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center"
          >
            <img src="/iris-logo.png" alt="Iris" className="h-6 w-auto object-contain opacity-80" />
          </a>
          <div className="flex items-center gap-2 bg-gray-100 rounded-full p-1 w-fit">
            {(['TR', 'EN'] as Lang[]).map((l) => (
              <button
                key={l}
                onClick={() => setLang(l)}
                className={`px-3 py-1 text-xs font-semibold tracking-wider uppercase rounded-full transition-all duration-300 cursor-pointer ${
                  lang === l ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-400'
                }`}
              >
                {l}
              </button>
            ))}
          </div>
          <a
            href="#contact"
            onClick={(e) => { e.preventDefault(); handleNav('#contact'); }}
            className="mt-2 px-6 py-3 bg-gray-900 text-white text-sm font-semibold tracking-wide rounded-full text-center cursor-pointer"
          >
            {nav.cta}
          </a>
        </div>
      )}
    </nav>
  );
}
